import { IMainField } from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import { TReactSelectOption } from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import defaultPtkmConfig, {
	defaultCalendar,
	defaultCopyFormData,
	defaultFormData,
	IStatusField,
} from "./defaults"
import _ from "lodash"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import getStoreProperty from "components/ProjectNonSpecificComponents/DevTools/Functions/getStoreProperty"
import { TStringArrayValuesObject } from "components/ProjectSpecificComponents/Interfaces/GlobalInterface"

/**
 * Global state management (mobx store) for managing all states in Admin -> PTKM section
 */
export default function createStore() {
	return {
		settings: settings,
		/**
		 * Function to get all configuration data and handle errors
		 * Latest is used to refetch data in certain situations
		 */
		async fetchSettings(getLatestData?: boolean) {
			const { formsConfigsPTKM, calendar } = this.settings

			var firstRead = formsConfigsPTKM.length === 0
			var noDateInfo = !calendar?.holidays && !calendar?.transferedDays

			const fetchLatestData = getLatestData || firstRead || noDateInfo

			// optimize fetching
			if (fetchLatestData) {
				const { ok, data, statusCode } = await AppRequests("PTKM").get<TptkmConfig>()

				if (ok && data) {
					this.settings = { ...data }
				} else {
					return { statusCode: statusCode, ok: false }
				}
			}

			return {
				calendar: this.settings.calendar,
				configuration: formsConfigsPTKM,
				ok: true,
			}
		},
		configFormBeforeEdit: defaultFormData,
		ptkmConfig: defaultFormData,

		setConfig(config: IptkmFormConfig) {
			this.ptkmConfig = config
			this.configFormBeforeEdit = config
		},
		// USED WHEN CREATING CONFIG FROM OTHER CONFIGS DATA
		copiedConfig: defaultCopyFormData,
		setCopiedConfig(config: IptkmFormConfig | undefined) {
			return (this.copiedConfig = config)
		},
		closeConfig() {
			this.ptkmConfig = defaultFormData
			this.configFormBeforeEdit = defaultFormData
		},
		checkIfFormIsEdited() {
			return _.isEqual(
				JSON.parse(JSON.stringify(this.ptkmConfig)),
				JSON.parse(JSON.stringify(this.configFormBeforeEdit))
			)
		},
		updateConfigBeforeUpdate() {
			this.configFormBeforeEdit = this.ptkmConfig
		},

		/**
		 * function to edit any section data
		 */
		editPTKMconfig(section: PTKMSections, sectionData: TSectionData) {
			// If this works, its a fucking miracle :)
			return (this.ptkmConfig = {
				...this.ptkmConfig,
				[section]: {
					...this.ptkmConfig[section],
					...sectionData,
				},
			})
		},

		/**
		 * Function to edit all hour inputs in "Deadline" section
		 */
		editDeadlineHours(
			yAxisType: "singleDimension" | string,
			deadlineType: "rlaHours" | "krHours",
			key: string | number,
			value: any
		) {
			const { priorityField } = this.ptkmConfig.deadlines
			const { rlaKrHours } = priorityField

			const mappedDeadlineTypeObject =
				deadlineType === "rlaHours"
					? { ...rlaKrHours[yAxisType]?.rlaHours }
					: { ...rlaKrHours[yAxisType]?.krHours }

			this.editPTKMconfig("deadlines", {
				priorityField: {
					...priorityField,
					rlaKrHours: {
						...priorityField.rlaKrHours,
						[yAxisType]: {
							...priorityField.rlaKrHours[yAxisType],
							[deadlineType]: {
								...mappedDeadlineTypeObject,
								[key]: value,
							},
						},
					},
				},
			})
		},

		calendar: defaultCalendar,
		editCalendar({ holidays, transferedDays, week }: ICalendar) {
			this.calendar = {
				holidays,
				transferedDays,
				week,
			}
		},

		editWorkingHours(day: TWeekDay, dayData: TDay) {
			this.editCalendar({
				...this.calendar,
				week: {
					...this.calendar.week,
					[day]: {
						...this.calendar.week[day],
						...dayData,
					},
				},
			})
		},

		allProjects: allProjects,
		setAllProjects(data: TReactSelectOption[]) {
			this.allProjects = data
		},

		allItems: allItems,
		setAllItems(data: TReactSelectOption[]) {
			this.allItems = data
		},

		fields: fields,
		setFields(data: IMainField[]) {
			this.fields = data
			this._updateMainFOption()
		},

		// All fields
		mainFOptions: mainFOptions,
		// Only picklists
		mainSelectOptions: mainFOptions,
		/**
		 * Each section has a main field select, which is used for rest of the section.
		 * F.E. First section "Deadlines" for "Picklist for priority"
		 * This function is used in all sections so same loop wouldnt be run multiple times
		 */
		get mainFieldOptions() {
			// If is set already
			if (this.mainFOptions) return { all: this.mainFOptions, picklist: this.mainSelectOptions }

			// Catch failed BE req. that retuns all options
			if (!this.fields || this.fields.length < 1) {
				this.mainFOptions = undefined
				this.mainSelectOptions = undefined
				return { all: this.mainFOptions, picklist: this.mainSelectOptions }
			}

			this._updateMainFOption()

			return { all: this.mainFOptions, picklist: this.mainSelectOptions }
		},

		_updateMainFOption() {
			const _allOptions: TReactSelectOption[] = []
			const _pickListOptions: TReactSelectOption[] = []

			this.fields.map((f) => {
				const singleOption: TReactSelectOption = {
					label: f.name,
					value: f.fieldId,
					extraProp: JSON.stringify(f.picklistValues),
				}
				_allOptions.push(singleOption)
				_pickListOptions.push({ ...singleOption, isDisabled: !f.picklistValues })
			})

			this.mainFOptions = _allOptions
			this.mainSelectOptions = _pickListOptions
		},
	}
}

const allProjects: [] | TReactSelectOption[] = []
const allItems: [] | TReactSelectOption[] = []
const fields: [] | IMainField[] = []
const mainFOptions: TReactSelectOption[] | undefined = undefined
const settings: TptkmConfig = defaultPtkmConfig
export type TSectionData = Partial<TDeadlines> | Partial<TptkmStatuses> | Partial<TResults>

export type TNewPtkmConfigData = {
	section: PTKMSections
	position: number
	sectionData: TSectionData
}

export type PTKMSections = "deadlines" | "status" | "results"
/**
 * full PTKM configurations
 */

export type TptkmConfig = {
	formsConfigsPTKM: IptkmFormConfig[]
	calendar: ICalendar
}

export type TptkmActivationResource = {
	calculateFrom?: string
	config: IptkmFormConfig
}

/**
 * Config for calendar
 */

export type TWeekDay = keyof IWeek

export interface ICalendar {
	week: IWeek
	/**
	 * List of working days that are holidays - "dd/mm/yyyy, dd/mm/yyyy, dd/mm/yyyy..."
	 */
	holidays: string
	/**
	 * List of working days that are transfered - "dd/mm/yyyy, dd/mm/yyyy, dd/mm/yyyy..."
	 */
	transferedDays: string
}

export interface IWeek {
	monday: TDay
	tuesday: TDay
	wednesday: TDay
	thursday: TDay
	friday: TDay
	saturday: TDay
	sunday: TDay
}

export type TDay = {
	start?: string
	end?: string
	workingDay: boolean
}

/**
 * Config for opened configuration
 */
export interface IptkmFormConfig {
	item: string
	itemId: string
	project: string
	projectId: string
	id: string
	name: string | null
	position: number
	isActive: boolean
	deadlines: TDeadlines
	status: TptkmStatuses
	results: TResults
}

export type THourType = {
	[type: string]: THours
}

export type TPriorityField = {
	fieldKey: string
	rlaKrHours: THourType
	secondFieldKey: string
}
/**
 * Types for deadlines
 */
export type TDeadlines = {
	/**
	 * item field with RLA/KR hours
	 */
	priorityField: TPriorityField
	/**
	 * Checbox to trigger PTKM deadline recalculation on all connected items
	 */
	// recalculateNewValues: boolean
}

export interface IHour {
	[key: string]: string
}

export type THours = {
	rlaHours: IHour
	krHours: IHour
}

/**
 * Types for status
 */
export type TptkmStatuses = {
	/**
	 *  Pick-list with all statuses
	 */
	statusPickList: IStatusField
	/**
	 *  Status that will trigger countdown to start
	 */
	startStatus: TReactSelectOption[] | null
	/**
	 *  Status that will trigger countdown to be paused
	 */
	pauseStatus: TReactSelectOption[] | null
	/**
	 * Status that will trigger countdown to stop
	 */
	krStopStatus: TReactSelectOption[] | null
	rlaStopStatus: TReactSelectOption[] | null
	/**
	 *  Field that will trigger countdown when it has values
	 */
	startWhenFieldHasValues: IStatusField
}

/**
 * Type for the result section in PTKM config
 */
export type TResults = {
	rlaStatuses: IrlaStatuses
	krStatuses: IkrStatuses
}
/**
 * Type for RLA (Reakcijas laika atskaite)
 */
export interface IrlaStatuses extends IDeadlineStatuses {
	/**
	 * Field that is configured as RLA field
	 */
	rlaField: string | undefined
	/**
	 * Field where PTKM will put calculated deadline for RLA
	 */
	rlaDeadlineDateField: string | undefined
}

/**
 * Type for KR (Kļūdas risinājuma)
 */
export interface IkrStatuses extends IDeadlineStatuses {
	/**
	 * Field that is configured as KR field
	 */
	krField: string | undefined
	/**
	 * Status when KR is not requred for KR Field
	 */
	notRequired: string | undefined
	/**
	 * Field where PTKM will put calculated deadline for KR
	 */
	krDeadlineDateField: string | undefined
}

export interface IDeadlineStatuses {
	/**
	 * Status when RZ is unedited
	 */
	unedited: string | undefined
	/**
	 * Status when RZ is submitted
	 */
	submitted: string | undefined
	/**
	 * Status when RZ has x hours left till deadline
	 */
	xHoursLeft: string | undefined
	/**
	 * Status when RZ is past its deadline
	 */
	late: string | undefined
	/**
	 * Status when RZ is submitted but after deadline
	 */
	submittedLate: string | undefined
	/**
	 * extra property for field xHoursLeft
	 */
	customHours: string | undefined
}
